import React, { useEffect, useState, useMemo } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Eye, Download, FileText, Trash2, History, Edit, Plus, Trash, FileSpreadsheet, Filter, X, AlertTriangle, Users } from 'lucide-react';

export default function ApplicationsList() {
  const { getAuthHeaders, user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [collegeFilter, setCollegeFilter] = useState('all');
  const [sessionFilter, setSessionFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [agencyFilter, setAgencyFilter] = useState('all');  // New: Agency filter for role-based filtering
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAuditOpen, setIsAuditOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [statusUpdate, setStatusUpdate] = useState({ status: '', payment_status: '', eligibility_status: '', remarks: '' });
  const [editFormData, setEditFormData] = useState({});
  const [editAcademicRecords, setEditAcademicRecords] = useState([]);
  const [editFiles, setEditFiles] = useState({});
  const [saving, setSaving] = useState(false);
  const [dropdownControls, setDropdownControls] = useState({});
  const [colleges, setColleges] = useState([]);
  const [enrollmentNumber, setEnrollmentNumber] = useState('');
  const [enrollmentRemarks, setEnrollmentRemarks] = useState('');
  const [savingEnrollment, setSavingEnrollment] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState({ session: '', college_id: '', month: '', agency_id: '', master_agency_id: '' });
  const [agencies, setAgencies] = useState([]);
  const [masterAgencies, setMasterAgencies] = useState([]);
  const [exporting, setExporting] = useState(false);

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchApplications();
    fetchDropdownControls();
    fetchColleges();
    fetchAgenciesForFilter();
    if (user?.role === 'admin' || user?.role === 'sub_admin') {
      fetchMasterAgencies();
    }
  }, [user?.role]);

  const fetchApplications = async () => {
    try {
      let url = `${API_URL}/applications`;
      if (user?.role === 'master_agency') {
        url = `${API_URL}/master-agency/applications`;
      }
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownControls = async () => {
    try {
      const response = await axios.get(`${API_URL}/dropdown-controls`, { headers: getAuthHeaders() });
      const controlsMap = {};
      response.data.forEach(control => { controlsMap[control.category] = control.values; });
      setDropdownControls(controlsMap);
    } catch (error) { console.error('Failed to fetch dropdown controls'); }
  };

  const fetchColleges = async () => {
    try {
      const response = await axios.get(`${API_URL}/colleges`, { headers: getAuthHeaders() });
      setColleges(response.data);
    } catch (error) { console.error('Failed to fetch colleges'); }
  };

  const fetchAgenciesForFilter = async () => {
    try {
      let url = `${API_URL}/agencies`;
      if (user?.role === 'master_agency') {
        url = `${API_URL}/master-agency/agencies`;
      }
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setAgencies(response.data);
    } catch (error) { console.error('Failed to fetch agencies'); }
  };

  const fetchMasterAgencies = async () => {
    try {
      const response = await axios.get(`${API_URL}/users?role=master_agency`, { headers: getAuthHeaders() });
      setMasterAgencies(response.data);
    } catch (error) { console.error('Failed to fetch master agencies'); }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportFilters.session) params.append('session', exportFilters.session);
      if (exportFilters.college_id) params.append('college_id', exportFilters.college_id);
      if (exportFilters.month) params.append('month', exportFilters.month);
      if (exportFilters.agency_id) params.append('agency_id', exportFilters.agency_id);
      if (exportFilters.master_agency_id) params.append('master_agency_id', exportFilters.master_agency_id);

      const response = await axios.get(`${API_URL}/export/applications?${params.toString()}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `student_applications_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Excel exported successfully');
      setIsExportOpen(false);
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const getAvailableSessions = () => {
    const sessions = new Set();
    applications.forEach(app => {
      if (app.admission_session) sessions.add(app.admission_session);
    });
    return Array.from(sessions).sort();
  };

  const getAvailableMonths = () => {
    const months = [];
    const currentDate = new Date();
    for (let i = 0; i < 24; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      months.push({
        value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
      });
    }
    return months;
  };

  // Filter applications based on all filters
  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      // Status filter
      if (statusFilter !== 'all' && app.status !== statusFilter) return false;
      
      // College filter
      if (collegeFilter !== 'all' && app.college_id !== collegeFilter) return false;
      
      // Session filter
      if (sessionFilter !== 'all' && app.admission_session !== sessionFilter) return false;
      
      // Month filter
      if (monthFilter !== 'all' && app.created_at) {
        const appDate = new Date(app.created_at);
        const appMonth = `${appDate.getFullYear()}-${String(appDate.getMonth() + 1).padStart(2, '0')}`;
        if (appMonth !== monthFilter) return false;
      }
      
      // Agency filter (role-based)
      if (agencyFilter !== 'all' && app.agency_id !== agencyFilter) return false;
      
      return true;
    });
  }, [applications, statusFilter, collegeFilter, sessionFilter, monthFilter, agencyFilter]);

  const clearAllFilters = () => {
    setStatusFilter('all');
    setCollegeFilter('all');
    setSessionFilter('all');
    setMonthFilter('all');
    setAgencyFilter('all');
  };

  const activeFilterCount = [statusFilter, collegeFilter, sessionFilter, monthFilter, agencyFilter].filter(f => f !== 'all').length;

  const handleViewDetails = async (appId) => {
    try {
      const response = await axios.get(`${API_URL}/applications/${appId}`, { headers: getAuthHeaders() });
      setSelectedApp(response.data);
      setStatusUpdate({
        status: response.data.status,
        payment_status: response.data.payment_status,
        eligibility_status: response.data.eligibility_status || 'pending',
        remarks: ''
      });
      setEnrollmentNumber(response.data.enrollment_number || '');
      setEnrollmentRemarks('');
      setIsDetailsOpen(true);
    } catch (error) {
      toast.error('Failed to fetch application details');
    }
  };

  const handleEditApplication = async (appId) => {
    try {
      const response = await axios.get(`${API_URL}/applications/${appId}`, { headers: getAuthHeaders() });
      const app = response.data;
      setSelectedApp(app);
      setEditFormData({
        student_name: app.student_name || '',
        father_name: app.father_name || '',
        mother_name: app.mother_name || '',
        gender: app.gender || '',
        dob: app.dob || '',
        category: app.category || '',
        email: app.email || '',
        mobile: app.mobile || '',
        aadhaar_no: app.aadhaar_no || '',
        deb_id: app.deb_id || '',
        apaar_abc_id: app.apaar_abc_id || '',
        marital_status: app.marital_status || '',
        employment_status: app.employment_status || '',
        religion: app.religion || '',
        alternate_email: app.alternate_email || '',
        alternate_mobile: app.alternate_mobile || '',
        address: app.address || '',
        pincode: app.pincode || '',
        post_office: app.post_office || '',
        district: app.district || '',
        state: app.state || '',
        college_id: app.college_id || '',
        admission_session: app.admission_session || '',
        admission_type: app.admission_type || '',
        course: app.course || '',
        specialization: app.specialization || ''
      });
      setEditAcademicRecords(app.academic_records || []);
      setEditFiles({});
      setIsEditOpen(true);
    } catch (error) {
      toast.error('Failed to fetch application details');
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const formDataToSend = new FormData();
      
      Object.keys(editFormData).forEach(key => {
        if (editFormData[key]) {
          formDataToSend.append(key, editFormData[key]);
        }
      });
      
      formDataToSend.append('academic_records', JSON.stringify(editAcademicRecords));
      
      Object.keys(editFiles).forEach(key => {
        if (editFiles[key]) {
          formDataToSend.append(key, editFiles[key]);
        }
      });

      // Use correct endpoint based on user role
      let url = `${API_URL}/applications/${selectedApp.id}`;
      if (user?.role === 'master_agency') {
        url = `${API_URL}/master-agency/applications/${selectedApp.id}`;
      }

      await axios.put(url, formDataToSend, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Application updated successfully');
      setIsEditOpen(false);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update application');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async (appId) => {
    try {
      const response = await axios.get(`${API_URL}/applications/${appId}/download-pdf`, { headers: getAuthHeaders() });
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${response.data.content}`;
      link.download = response.data.filename;
      link.click();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const response = await axios.get(`${API_URL}/files/${fileId}`, { headers: getAuthHeaders() });
      const link = document.createElement('a');
      link.href = `data:application/octet-stream;base64,${response.data.content}`;
      link.download = response.data.filename || filename;
      link.click();
      toast.success('File downloaded successfully');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDeleteClick = (app) => {
    setAppToDelete(app);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteApplication = async () => {
    if (!appToDelete) return;
    try {
      // Use correct endpoint based on user role
      let url = `${API_URL}/applications/${appToDelete.id}`;
      if (user?.role === 'master_agency') {
        url = `${API_URL}/master-agency/applications/${appToDelete.id}`;
      }
      
      await axios.delete(url, { headers: getAuthHeaders() });
      toast.success('Application moved to trash successfully');
      setIsDeleteConfirmOpen(false);
      setAppToDelete(null);
      setIsDetailsOpen(false);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete application');
    }
  };

  const handleViewAuditLogs = async (appId) => {
    try {
      const response = await axios.get(`${API_URL}/applications/${appId}/audit-logs`, { headers: getAuthHeaders() });
      setAuditLogs(response.data);
      setIsAuditOpen(true);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    }
  };

  const handleUpdateStatus = async () => {
    try {
      // Use correct endpoint based on user role
      let url = `${API_URL}/applications/${selectedApp.id}/status`;
      if (user?.role === 'master_agency') {
        url = `${API_URL}/master-agency/applications/${selectedApp.id}/status`;
      }
      
      await axios.put(url, statusUpdate, { headers: getAuthHeaders() });
      toast.success('Application status updated');
      setIsDetailsOpen(false);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleUpdateEnrollmentNumber = async () => {
    if (!enrollmentNumber.trim()) {
      toast.error('Please enter an enrollment number');
      return;
    }
    setSavingEnrollment(true);
    try {
      await axios.put(`${API_URL}/applications/${selectedApp.id}/enrollment-number`, {
        enrollment_number: enrollmentNumber.trim(),
        remarks: enrollmentRemarks || null
      }, { headers: getAuthHeaders() });
      toast.success('Enrollment number updated successfully');
      setSelectedApp({ ...selectedApp, enrollment_number: enrollmentNumber.trim() });
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update enrollment number');
    } finally {
      setSavingEnrollment(false);
    }
  };

  const updateEditAcademicRecord = (index, field, value) => {
    const updated = [...editAcademicRecords];
    updated[index][field] = value;
    setEditAcademicRecords(updated);
  };

  const addEditAcademicRecord = () => {
    setEditAcademicRecords([...editAcademicRecords, {
      exam_name: '', subject: '', year: '', board_university: '', obtained_marks: '', max_marks: '', percentage_grade: ''
    }]);
  };

  const removeEditAcademicRecord = (index) => {
    setEditAcademicRecords(editAcademicRecords.filter((_, i) => i !== index));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'successful': case 'approved': return 'bg-green-100 text-green-700';
      case 'unsuccessful': case 'rejected': return 'bg-red-100 text-red-700';
      case 'on_hold': return 'bg-orange-100 text-orange-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'completed': case 'released': return 'bg-green-100 text-green-700';
      case 'ineligible': return 'bg-gray-100 text-gray-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  const InfoRow = ({ label, value }) => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-slate-100">
      <div className="text-xs sm:text-sm font-medium text-slate-500">{label}</div>
      <div className="sm:col-span-2 text-sm text-slate-900">{value || '-'}</div>
    </div>
  );

  const DocumentDownloadRow = ({ label, fileId }) => fileId ? (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 p-3 border border-slate-200 rounded">
      <span className="text-sm text-slate-700">{label}</span>
      <Button size="sm" variant="outline" onClick={() => handleDownloadFile(fileId, label.replace(/\s+/g, '_').toLowerCase())} className="w-full sm:w-auto">
        <Download className="w-4 h-4 mr-1" /> Download
      </Button>
    </div>
  ) : null;

  return (
    <Sidebar>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Applications</h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1 sm:mt-2">View and manage admission applications</p>
          </div>
          <Button 
            onClick={() => setIsExportOpen(true)} 
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="export-excel-button"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>

        <div className="mb-6">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-purple-600" />
                <span className="font-semibold text-slate-900">Filters</span>
                {activeFilterCount > 0 && (
                  <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-1 rounded-full">
                    {activeFilterCount} active
                  </span>
                )}
              </div>
              {activeFilterCount > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearAllFilters}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {/* Status Filter */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* College Filter */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">College / University</Label>
                <Select value={collegeFilter} onValueChange={setCollegeFilter}>
                  <SelectTrigger data-testid="college-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Colleges</SelectItem>
                    {colleges.map(college => (
                      <SelectItem key={college.id} value={college.id}>{college.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Session Filter */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Admission Session</Label>
                <Select value={sessionFilter} onValueChange={setSessionFilter}>
                  <SelectTrigger data-testid="session-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sessions</SelectItem>
                    {getAvailableSessions().map(session => (
                      <SelectItem key={session} value={session}>{session}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Month Filter */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Submission Month</Label>
                <Select value={monthFilter} onValueChange={setMonthFilter}>
                  <SelectTrigger data-testid="month-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    {getAvailableMonths().map(month => (
                      <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Agency Filter - Role Based */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {user?.role === 'master_agency' ? 'Agency' : 'Filter by Agency'}
                </Label>
                <Select value={agencyFilter} onValueChange={setAgencyFilter}>
                  <SelectTrigger data-testid="agency-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Agencies</SelectItem>
                    {agencies.map(agency => (
                      <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Results count */}
            <div className="mt-4 pt-3 border-t border-slate-100">
              <p className="text-sm text-slate-600">
                Showing <span className="font-semibold text-purple-600">{filteredApplications.length}</span> of {applications.length} applications
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading applications...</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="applications-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">App Number</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Enrollment No.</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Student Name</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">College</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Course</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Session</th>
                    {(user?.role === 'admin' || user?.role === 'sub_admin' || user?.role === 'master_agency') && (
                      <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Agency</th>
                    )}
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Payment</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplications.map((app) => (
                    <tr key={app.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`application-row-${app.id}`}>
                      <td className="p-4 align-middle text-sm text-slate-700 font-medium whitespace-nowrap">{app.application_number}</td>
                      <td className="p-4 align-middle text-sm whitespace-nowrap">
                        {app.enrollment_number ? (
                          <span className="inline-block px-2 py-1 text-xs rounded bg-purple-100 text-purple-700 font-medium">{app.enrollment_number}</span>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="p-4 align-middle text-sm text-slate-700 whitespace-nowrap">{app.student_name}</td>
                      <td className="p-4 align-middle text-sm text-slate-600 whitespace-nowrap">{app.college_name}</td>
                      <td className="p-4 align-middle text-sm text-slate-600 whitespace-nowrap">{app.course} - {app.specialization}</td>
                      <td className="p-4 align-middle text-sm text-slate-600 whitespace-nowrap">{app.admission_session}</td>
                      {(user?.role === 'admin' || user?.role === 'sub_admin' || user?.role === 'master_agency') && (
                        <td className="p-4 align-middle text-sm text-slate-700 font-medium whitespace-nowrap">{app.agency_name}</td>
                      )}
                      <td className="p-4 align-middle text-sm whitespace-nowrap">
                        <span className={`inline-block px-2 py-1 text-xs rounded ${getStatusColor(app.status)}`}>{app.status.toUpperCase()}</span>
                      </td>
                      <td className="p-4 align-middle text-sm whitespace-nowrap">
                        <span className={`inline-block px-2 py-1 text-xs rounded ${getPaymentStatusColor(app.payment_status)}`}>{app.payment_status.toUpperCase()}</span>
                      </td>
                      <td className="p-4 align-middle text-sm whitespace-nowrap">
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => handleViewDetails(app.id)} data-testid={`view-app-${app.id}`} title="View Details"><Eye className="w-4 h-4" /></Button>
                          <Button size="sm" variant="outline" onClick={() => handleDownloadPDF(app.id)} title="Download PDF"><FileText className="w-4 h-4" /></Button>
                          {(user?.role === 'admin' || user?.role === 'sub_admin' || user?.role === 'master_agency') && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => handleEditApplication(app.id)} data-testid={`edit-app-${app.id}`} title="Edit Application" className="text-blue-600"><Edit className="w-4 h-4" /></Button>
                              <Button size="sm" variant="outline" onClick={() => handleViewAuditLogs(app.id)} title="View Audit Logs"><History className="w-4 h-4" /></Button>
                              <Button size="sm" variant="outline" onClick={() => handleDeleteClick(app)} className="text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredApplications.length === 0 && (
              <div className="text-center py-12">
                <p className="text-slate-500 mb-2">No applications match your filters.</p>
                {activeFilterCount > 0 && (
                  <Button variant="outline" size="sm" onClick={clearAllFilters}>
                    Clear Filters
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* View Details Dialog */}
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Application Details - {selectedApp?.application_number}</DialogTitle></DialogHeader>
            {selectedApp && (
              <div className="space-y-6">
                {/* Enrollment Number Section - Visible to All */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-purple-900 mb-3">Student Enrollment Number</h3>
                  {(user?.role === 'admin' || user?.role === 'sub_admin') ? (
                    <div className="space-y-3">
                      <div className="flex gap-3 items-end">
                        <div className="flex-1">
                          <Label className="text-purple-700">Enrollment Number</Label>
                          <Input 
                            value={enrollmentNumber} 
                            onChange={(e) => setEnrollmentNumber(e.target.value)}
                            placeholder="Enter student enrollment number"
                            className="border-purple-300 focus:ring-purple-500"
                          />
                        </div>
                        <Button 
                          onClick={handleUpdateEnrollmentNumber} 
                          disabled={savingEnrollment}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          {savingEnrollment ? 'Saving...' : (selectedApp.enrollment_number ? 'Update' : 'Add')}
                        </Button>
                      </div>
                      <div>
                        <Label className="text-purple-700">Remarks (Optional)</Label>
                        <Input 
                          value={enrollmentRemarks} 
                          onChange={(e) => setEnrollmentRemarks(e.target.value)}
                          placeholder="Add remarks for this update"
                          className="border-purple-300 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      {selectedApp.enrollment_number ? (
                        <span className="text-2xl font-bold text-purple-700">{selectedApp.enrollment_number}</span>
                      ) : (
                        <span className="text-slate-500 italic">Enrollment number not assigned yet</span>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 pb-2 border-b">1. Admission Details</h3>
                  <InfoRow label="College/University" value={selectedApp.college_name} />
                  <InfoRow label="Session" value={selectedApp.admission_session} />
                  <InfoRow label="Type" value={selectedApp.admission_type} />
                  <InfoRow label="Course" value={`${selectedApp.course} - ${selectedApp.specialization}`} />
                  {(user?.role === 'admin' || user?.role === 'sub_admin') && <InfoRow label="Submitted By" value={selectedApp.agency_name} />}
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 pb-2 border-b">2. Personal Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                    <div><InfoRow label="Student Name" value={selectedApp.student_name} /><InfoRow label="Father" value={selectedApp.father_name} /><InfoRow label="Mother" value={selectedApp.mother_name} /><InfoRow label="Gender" value={selectedApp.gender} /><InfoRow label="DOB" value={selectedApp.dob} /><InfoRow label="Category" value={selectedApp.category} /></div>
                    <div><InfoRow label="Email" value={selectedApp.email} /><InfoRow label="Mobile" value={selectedApp.mobile} /><InfoRow label="Aadhaar" value={selectedApp.aadhaar_no} /><InfoRow label="APAAR/ABC ID" value={selectedApp.apaar_abc_id} /><InfoRow label="Marital Status" value={selectedApp.marital_status} /><InfoRow label="Religion" value={selectedApp.religion} /></div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 pb-2 border-b">3. Address</h3>
                  <InfoRow label="Address" value={selectedApp.address} />
                  <div className="grid grid-cols-2 gap-x-8"><InfoRow label="Pincode" value={selectedApp.pincode} /><InfoRow label="District" value={selectedApp.district} /><InfoRow label="State" value={selectedApp.state} /></div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 pb-2 border-b">4. Academic Details</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-slate-200">
                      <thead className="bg-slate-50"><tr><th className="border px-3 py-2 text-left text-xs">Exam</th><th className="border px-3 py-2 text-left text-xs">Subject</th><th className="border px-3 py-2 text-left text-xs">Year</th><th className="border px-3 py-2 text-left text-xs">Board</th><th className="border px-3 py-2 text-left text-xs">Obtained</th><th className="border px-3 py-2 text-left text-xs">Max</th><th className="border px-3 py-2 text-left text-xs">%</th></tr></thead>
                      <tbody>{selectedApp.academic_records?.map((r, i) => (<tr key={i}><td className="border px-3 py-2 text-sm">{r.exam_name}</td><td className="border px-3 py-2 text-sm">{r.subject}</td><td className="border px-3 py-2 text-sm">{r.year}</td><td className="border px-3 py-2 text-sm">{r.board_university}</td><td className="border px-3 py-2 text-sm">{r.obtained_marks}</td><td className="border px-3 py-2 text-sm">{r.max_marks}</td><td className="border px-3 py-2 text-sm font-medium">{r.percentage_grade}</td></tr>))}</tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 pb-2 border-b">5. Documents</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DocumentDownloadRow label="Student Photo" fileId={selectedApp.student_photo_id} />
                    <DocumentDownloadRow label="Student Signature" fileId={selectedApp.student_signature_id} />
                    <DocumentDownloadRow label="Address/ID Proof" fileId={selectedApp.address_id_proof_id} />
                    <DocumentDownloadRow label="ABC/DEB Screenshot" fileId={selectedApp.abc_deb_screenshot_id} />
                    <DocumentDownloadRow label="Other Document" fileId={selectedApp.other_document_id} />
                    <DocumentDownloadRow label="10th Marksheet" fileId={selectedApp.tenth_marksheet_id} />
                    <DocumentDownloadRow label="12th Marksheet" fileId={selectedApp.twelfth_marksheet_id} />
                    <DocumentDownloadRow label="Graduation Marksheet" fileId={selectedApp.graduation_marksheet_id} />
                  </div>
                </div>

                {(user?.role === 'admin' || user?.role === 'sub_admin' || user?.role === 'master_agency') && (
                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Update Status</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div><label className="text-sm font-medium text-slate-700 mb-2 block">Application Status</label><Select value={statusUpdate.status} onValueChange={(v) => setStatusUpdate({...statusUpdate, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem><SelectItem value="on_hold">On Hold</SelectItem></SelectContent></Select></div>
                      <div><label className="text-sm font-medium text-slate-700 mb-2 block">Payment Status</label><Select value={statusUpdate.payment_status} onValueChange={(v) => setStatusUpdate({...statusUpdate, payment_status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="released">Released</SelectItem><SelectItem value="ineligible">Ineligible</SelectItem></SelectContent></Select></div>
                      <div><label className="text-sm font-medium text-slate-700 mb-2 block">Eligibility</label><Select value={statusUpdate.eligibility_status} onValueChange={(v) => setStatusUpdate({...statusUpdate, eligibility_status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="eligible">Eligible</SelectItem><SelectItem value="not_eligible">Not Eligible</SelectItem></SelectContent></Select></div>
                    </div>
                    <div className="mt-4"><Label>Remarks</Label><Textarea value={statusUpdate.remarks} onChange={(e) => setStatusUpdate({...statusUpdate, remarks: e.target.value})} rows={3} /></div>
                    <div className="flex justify-between mt-4">
                      <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => handleDeleteClick(selectedApp)}>
                        <Trash2 className="w-4 h-4 mr-1" />Delete Application
                      </Button>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => handleViewAuditLogs(selectedApp.id)}><History className="w-4 h-4 mr-1" />History</Button>
                        <Button onClick={handleUpdateStatus} className="bg-[#0052CC] text-white">Update Status</Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Application Dialog - Admin/SubAdmin/Master Agency */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Application - {selectedApp?.application_number}</DialogTitle></DialogHeader>
            <div className="space-y-6">
              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Personal Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label>Student Name</Label><Input value={editFormData.student_name} onChange={(e) => setEditFormData({...editFormData, student_name: e.target.value})} /></div>
                  <div><Label>Father's Name</Label><Input value={editFormData.father_name} onChange={(e) => setEditFormData({...editFormData, father_name: e.target.value})} /></div>
                  <div><Label>Mother's Name</Label><Input value={editFormData.mother_name} onChange={(e) => setEditFormData({...editFormData, mother_name: e.target.value})} /></div>
                  <div><Label>Email</Label><Input type="email" value={editFormData.email} onChange={(e) => setEditFormData({...editFormData, email: e.target.value})} /></div>
                  <div><Label>Mobile</Label><Input value={editFormData.mobile} onChange={(e) => setEditFormData({...editFormData, mobile: e.target.value})} /></div>
                  <div><Label>DOB</Label><Input value={editFormData.dob} onChange={(e) => setEditFormData({...editFormData, dob: e.target.value})} /></div>
                  <div><Label>Gender</Label><Select value={editFormData.gender} onValueChange={(v) => setEditFormData({...editFormData, gender: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{(dropdownControls.genders || ['Male', 'Female', 'Other']).map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Category</Label><Select value={editFormData.category} onValueChange={(v) => setEditFormData({...editFormData, category: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{(dropdownControls.categories || ['General', 'OBC', 'SC', 'ST']).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>APAAR/ABC ID</Label><Input value={editFormData.apaar_abc_id} onChange={(e) => setEditFormData({...editFormData, apaar_abc_id: e.target.value})} /></div>
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Address Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2"><Label>Address</Label><Textarea value={editFormData.address} onChange={(e) => setEditFormData({...editFormData, address: e.target.value})} rows={2} /></div>
                  <div><Label>Pincode</Label><Input value={editFormData.pincode} onChange={(e) => setEditFormData({...editFormData, pincode: e.target.value})} /></div>
                  <div><Label>District</Label><Input value={editFormData.district} onChange={(e) => setEditFormData({...editFormData, district: e.target.value})} /></div>
                  <div><Label>State</Label><Input value={editFormData.state} onChange={(e) => setEditFormData({...editFormData, state: e.target.value})} /></div>
                  <div><Label>Post Office</Label><Input value={editFormData.post_office} onChange={(e) => setEditFormData({...editFormData, post_office: e.target.value})} /></div>
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-semibold">Academic Details</h3><Button type="button" size="sm" variant="outline" onClick={addEditAcademicRecord}><Plus className="w-4 h-4 mr-1" />Add</Button></div>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead><tr className="bg-white"><th className="border px-2 py-2 text-xs">Exam</th><th className="border px-2 py-2 text-xs">Subject</th><th className="border px-2 py-2 text-xs">Year</th><th className="border px-2 py-2 text-xs">Board</th><th className="border px-2 py-2 text-xs">Obtained</th><th className="border px-2 py-2 text-xs">Max</th><th className="border px-2 py-2 text-xs">%</th><th className="border px-2 py-2 text-xs">Del</th></tr></thead>
                    <tbody>
                      {editAcademicRecords.map((r, i) => (
                        <tr key={i}>
                          <td className="border px-1 py-1"><Input value={r.exam_name} onChange={(e) => updateEditAcademicRecord(i, 'exam_name', e.target.value)} className="min-w-[100px]" /></td>
                          <td className="border px-1 py-1"><Input value={r.subject} onChange={(e) => updateEditAcademicRecord(i, 'subject', e.target.value)} className="min-w-[100px]" /></td>
                          <td className="border px-1 py-1"><Input value={r.year} onChange={(e) => updateEditAcademicRecord(i, 'year', e.target.value)} className="min-w-[80px]" /></td>
                          <td className="border px-1 py-1"><Input value={r.board_university} onChange={(e) => updateEditAcademicRecord(i, 'board_university', e.target.value)} className="min-w-[100px]" /></td>
                          <td className="border px-1 py-1"><Input value={r.obtained_marks} onChange={(e) => updateEditAcademicRecord(i, 'obtained_marks', e.target.value)} className="min-w-[60px]" /></td>
                          <td className="border px-1 py-1"><Input value={r.max_marks} onChange={(e) => updateEditAcademicRecord(i, 'max_marks', e.target.value)} className="min-w-[60px]" /></td>
                          <td className="border px-1 py-1"><Input value={r.percentage_grade} onChange={(e) => updateEditAcademicRecord(i, 'percentage_grade', e.target.value)} className="min-w-[60px]" /></td>
                          <td className="border px-1 py-1 text-center"><Button size="sm" variant="ghost" onClick={() => removeEditAcademicRecord(i)} className="text-red-600"><Trash className="w-4 h-4" /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">Replace Documents (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label>Student Photo</Label><Input type="file" accept="image/*,.pdf" onChange={(e) => setEditFiles({...editFiles, student_photo: e.target.files[0]})} /></div>
                  <div><Label>Student Signature</Label><Input type="file" accept="image/*,.pdf" onChange={(e) => setEditFiles({...editFiles, student_signature: e.target.files[0]})} /></div>
                  <div><Label>Address/ID Proof</Label><Input type="file" accept="image/*,.pdf" onChange={(e) => setEditFiles({...editFiles, address_id_proof: e.target.files[0]})} /></div>
                  <div><Label>ABC/DEB Screenshot</Label><Input type="file" accept="image/*,.pdf" onChange={(e) => setEditFiles({...editFiles, abc_deb_screenshot: e.target.files[0]})} /></div>
                  <div><Label>10th Marksheet</Label><Input type="file" accept="image/*,.pdf" onChange={(e) => setEditFiles({...editFiles, tenth_marksheet: e.target.files[0]})} /></div>
                  <div><Label>12th Marksheet</Label><Input type="file" accept="image/*,.pdf" onChange={(e) => setEditFiles({...editFiles, twelfth_marksheet: e.target.files[0]})} /></div>
                  <div><Label>Graduation Marksheet</Label><Input type="file" accept="image/*,.pdf" onChange={(e) => setEditFiles({...editFiles, graduation_marksheet: e.target.files[0]})} /></div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveEdit} disabled={saving} className="bg-[#0052CC] text-white">{saving ? 'Saving...' : 'Save Changes'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Audit Logs Dialog */}
        <Dialog open={isAuditOpen} onOpenChange={setIsAuditOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Audit History</DialogTitle></DialogHeader>
            <div className="space-y-4">
              {auditLogs.length > 0 ? auditLogs.map((log, i) => (
                <div key={i} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div><span className="text-sm font-medium">{log.action_type.replace(/_/g, ' ').toUpperCase()}</span><p className="text-xs text-slate-500 mt-1">by {log.changed_by} ({log.user_role})</p></div>
                    <span className="text-xs text-slate-500">{new Date(log.timestamp).toLocaleString()}</span>
                  </div>
                  {log.remarks && <p className="text-sm text-slate-600 italic">"{log.remarks}"</p>}
                </div>
              )) : <p className="text-center text-slate-500 py-8">No audit logs</p>}
            </div>
          </DialogContent>
        </Dialog>

        {/* Export Excel Dialog */}
        <Dialog open={isExportOpen} onOpenChange={setIsExportOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Export Student Data to Excel</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">Select filters to export specific data. Leave empty to export all data.</p>
              
              <div>
                <Label>Session</Label>
                <Select value={exportFilters.session || 'all'} onValueChange={(v) => setExportFilters({...exportFilters, session: v === 'all' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="All Sessions" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sessions</SelectItem>
                    {getAvailableSessions().map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>College</Label>
                <Select value={exportFilters.college_id || 'all'} onValueChange={(v) => setExportFilters({...exportFilters, college_id: v === 'all' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="All Colleges" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Colleges</SelectItem>
                    {colleges.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Month</Label>
                <Select value={exportFilters.month || 'all'} onValueChange={(v) => setExportFilters({...exportFilters, month: v === 'all' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="All Months" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    {getAvailableMonths().map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {(user?.role === 'admin' || user?.role === 'sub_admin') && (
                <>
                  <div>
                    <Label>Agency</Label>
                    <Select value={exportFilters.agency_id || 'all'} onValueChange={(v) => setExportFilters({...exportFilters, agency_id: v === 'all' ? '' : v})}>
                      <SelectTrigger><SelectValue placeholder="All Agencies" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Agencies</SelectItem>
                        {agencies.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Master Agency</Label>
                    <Select value={exportFilters.master_agency_id || 'all'} onValueChange={(v) => setExportFilters({...exportFilters, master_agency_id: v === 'all' ? '' : v})}>
                      <SelectTrigger><SelectValue placeholder="All Master Agencies" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Master Agencies</SelectItem>
                        {masterAgencies.map(m => <SelectItem key={m.id} value={m.id}>{m.name || m.username}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsExportOpen(false)}>Cancel</Button>
                <Button 
                  onClick={handleExportExcel} 
                  disabled={exporting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  {exporting ? 'Exporting...' : 'Export'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Delete Application
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-slate-600">
                Are you sure you want to delete application <strong>{appToDelete?.application_number}</strong>?
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Student: {appToDelete?.student_name}
              </p>
              <p className="text-sm text-slate-500 mt-4 bg-amber-50 border border-amber-200 rounded p-2">
                This will move the application to Trash. Admin can restore it if needed.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleDeleteApplication} 
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                Delete Application
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Sidebar>
  );
}
